import { useEffect, useMemo } from 'react'
import { useAuth } from '../../context/useAuth'
import { useRaid } from '../../context/useRaid'
import { useTimeline } from '../../context/useTimeline'
import { saveNativeWidgetCache } from '../../services/nativeWidgetBridge'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import type { ChecklistItem } from '../../types/schema'
import type { TimelineElement } from '../../types/timelineElement'
import type { Workspace } from '../../types/workspace'

const ACTIVE_STATUSES = new Set(['ASSIGNED', 'ACCEPTED', 'ASSESSING', 'ONGOING'])

const clamp = (value: number, min = 0, max = 100) => Math.max(min, Math.min(max, Math.round(value)))

const dueMs = (item: { scheduleEndDate?: string | null; scheduleStartDate?: string | null }) => {
  const raw = item.scheduleEndDate ?? item.scheduleStartDate
  if (!raw) return Number.POSITIVE_INFINITY
  const ms = Date.parse(raw)
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY
}

const dueLabel = (item: { scheduleEndDate?: string | null; scheduleStartDate?: string | null }) => {
  const ms = dueMs(item)
  if (!Number.isFinite(ms)) return 'No deadline'
  const diffHours = Math.ceil((ms - Date.now()) / 3_600_000)
  if (diffHours < 0) return `${Math.abs(diffHours)}h overdue`
  if (diffHours < 24) return `${diffHours}h remaining`
  return `${Math.ceil(diffHours / 24)}d remaining`
}

const itemStatus = (item: Pick<ChecklistItem, 'workflowStatus' | 'isCompleted' | 'requiresProofFile' | 'proofFileUrl'>) => {
  if (item.requiresProofFile && !item.proofFileUrl) return 'PROOF NEEDED'
  if (item.isCompleted || item.workflowStatus === 'COMPLETED') return 'COMPLETE'
  return item.workflowStatus ?? 'ASSIGNED'
}

const firstWarning = (item: Pick<ChecklistItem, 'requiresProofFile' | 'proofFileUrl' | 'blockedByNodeIds' | 'dependencyNodeIds' | 'isReady'>) => {
  if ((item.blockedByNodeIds?.length ?? 0) > 0 || (item.dependencyNodeIds?.length ?? 0) > 0) return 'Dependency check required'
  if (item.requiresProofFile && !item.proofFileUrl) return 'Proof verification required'
  if (item.isReady === false) return 'Resource readiness pending'
  return 'No critical warnings'
}

const stockSummary = (item: Pick<ChecklistItem, 'stockRequirements'>) => {
  const requirements = item.stockRequirements ?? []
  if (!requirements.length) return []
  return requirements.slice(0, 2).map((requirement) => {
    const qty = Number.isFinite(requirement.quantity) ? Math.max(1, Math.round(requirement.quantity)) : 1
    return `${qty} unit${qty === 1 ? '' : 's'} required`
  })
}

const isOpenChecklist = (item: ChecklistItem) =>
  !item.isCompleted && ACTIVE_STATUSES.has(item.workflowStatus ?? 'ASSIGNED')

const buildMemberPayload = (
  workspace: Workspace,
  elements: TimelineElement[],
  checklistItems: ChecklistItem[],
  uid: string | null,
  activeRaid: unknown,
) => {
  const workspaceElements = elements.filter((element) => element.workspaceId === workspace.id && element.board !== 'BIN' && element.board !== 'ARCHIVE')
  const workspaceChecklist = checklistItems.filter((item) => item.workspaceId === workspace.id)
  const openItems = workspaceChecklist.filter(isOpenChecklist)
  const completed = workspaceChecklist.filter((item) => item.isCompleted || item.workflowStatus === 'COMPLETED').length
  const total = Math.max(1, workspaceChecklist.length)
  const overdue = openItems.filter((item) => dueMs(item) < Date.now())
  const blocked = openItems.filter((item) => (item.blockedByNodeIds?.length ?? 0) > 0 || (item.dependencyNodeIds?.length ?? 0) > 0)
  const resourceIssues = openItems.filter((item) => item.isReady === false || (item.stockRequirements?.length ?? 0) > 0 && item.workflowStatus === 'ONGOING').length
  const avgProgress = workspaceElements.length
    ? workspaceElements.reduce((sum, element) => sum + clamp(Number(element.progress ?? 0)), 0) / workspaceElements.length
    : 0
  const readinessScore = clamp((completed / total) * 55 + avgProgress * 0.45 - overdue.length * 6 - blocked.length * 5 - resourceIssues * 3)
  const pendingVouches = workspaceChecklist.filter((item) =>
    (item.isCompleted || item.workflowStatus === 'COMPLETED') &&
    typeof item.assigneeId === 'string' &&
    item.assigneeId !== uid,
  ).length
  return {
    workspaceId: workspace.id,
    workspaceName: workspace.name,
    updatedAt: Date.now(),
    privacy: 'concise',
    pulse: {
      healthState: readinessScore < 45 || blocked.length > 4 ? 'CRITICAL' : readinessScore < 70 || overdue.length > 0 ? 'WATCH' : 'HEALTHY',
      readinessScore,
      criticalBlockers: blocked.length + overdue.length,
      activeRaids: activeRaid ? 1 : 0,
      resourceIssues,
    },
    command: {
      pendingVouches,
      alertStatus: 'Broadcast opens confirm',
    },
  }
}

const buildHelperPayload = (
  workspaceId: string | null,
  workspaceName: string | null,
  items: HelperAssignedItem[],
) => {
  const current = [...items].sort((left, right) => dueMs(left) - dueMs(right))[0] ?? null
  return {
    workspaceId,
    workspaceName,
    updatedAt: Date.now(),
    privacy: 'concise',
    pendingProofs: items.filter((item) => item.requiresProofFile && !item.proofFileUrl).length,
    currentTask: current ? {
      id: current.id,
      title: current.title,
      status: itemStatus(current),
      dueLabel: dueLabel(current),
      warning: firstWarning(current),
      inventory: stockSummary(current),
    } : null,
  }
}

export const MemberWidgetCachePublisher = ({ workspace }: { workspace: Workspace }) => {
  const { user } = useAuth()
  const { elements, checklistItems } = useTimeline()
  const { activeRaid } = useRaid()
  const payload = useMemo(
    () => buildMemberPayload(workspace, elements, checklistItems, user?.uid ?? null, activeRaid),
    [activeRaid, checklistItems, elements, user?.uid, workspace],
  )
  const signature = JSON.stringify(payload)

  useEffect(() => {
    void saveNativeWidgetCache('member', payload)
  }, [payload, signature])

  return null
}

export const HelperWidgetCachePublisher = ({
  workspaceId,
  workspaceName,
  items,
}: {
  workspaceId: string | null
  workspaceName: string | null
  items: HelperAssignedItem[]
}) => {
  const payload = useMemo(
    () => buildHelperPayload(workspaceId, workspaceName, items),
    [items, workspaceId, workspaceName],
  )
  const signature = JSON.stringify(payload)

  useEffect(() => {
    void saveNativeWidgetCache('helper', payload)
  }, [payload, signature])

  return null
}
